/**
 * Offline mutation queue — persistent queue for failed mutations.
 * Survives page reloads and retries when back online.
 *
 * Network errors → retry indéfiniment (l'utilisateur est juste hors-ligne)
 * Server errors (4xx) → marquer comme "failed" (données invalides, ne pas retenter)
 */

const QUEUE_KEY = "palnia_offline_queue";

export type MutationStatus = "pending" | "failed";

export interface QueuedMutation {
  id: string;
  type: "create" | "update" | "delete";
  entity: "task" | "event" | "habit" | "category" | "subtask";
  entityId: string;
  path: string;
  method: string;
  body?: string;
  createdAt: number;
  retries: number;
  status: MutationStatus;
  lastError?: string;
}

function getQueue(): QueuedMutation[] {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveQueue(mutations: QueuedMutation[]): void {
  if (mutations.length === 0) {
    localStorage.removeItem(QUEUE_KEY);
  } else {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(mutations));
  }
}

export function enqueue(mutation: Omit<QueuedMutation, "id" | "createdAt" | "retries" | "status">): void {
  const queue = getQueue();
  const newMutation: QueuedMutation = {
    ...mutation,
    id: crypto.randomUUID(),
    createdAt: Date.now(),
    retries: 0,
    status: "pending",
  };
  queue.push(newMutation);
  saveQueue(queue);
  console.log(`[offline-queue] Queued ${mutation.type} for ${mutation.entity}/${mutation.entityId}`);
}

export function dequeue(id: string): void {
  const queue = getQueue();
  const filtered = queue.filter((m) => m.id !== id);
  saveQueue(filtered);
}

export function getQueueSize(): number {
  return getQueue().length;
}

export function getPendingCount(): number {
  return getQueue().filter((m) => m.status === "pending").length;
}

export function getFailedMutations(): QueuedMutation[] {
  return getQueue().filter((m) => m.status === "failed");
}

export function retryFailed(): void {
  const queue = getQueue();
  for (const m of queue) {
    if (m.status === "failed") m.status = "pending";
  }
  saveQueue(queue);
}

export function removeMutation(id: string): void {
  dequeue(id);
}

export function clearQueue(): void {
  localStorage.removeItem(QUEUE_KEY);
}

function isNetworkError(err: unknown): boolean {
  return err instanceof TypeError && (err.message === "Failed to fetch" || err.message === "NetworkError when attempting to fetch resource.");
}

function isHarmlessError(err: unknown): boolean {
  const status = (err as { status?: number }).status;
  return status === 409 || status === 404;
}

function isServerRejection(err: unknown): boolean {
  const status = (err as { status?: number }).status;
  return typeof status === "number" && status >= 400 && status < 500 && !isHarmlessError(err);
}

export async function flushQueue(
  apiFetch: (path: string, options: RequestInit) => Promise<unknown>,
): Promise<{ succeeded: number; failed: number; rejected: number }> {
  const queue = getQueue();
  let succeeded = 0;
  let failed = 0;
  let rejected = 0;

  for (const mutation of queue) {
    if (mutation.status === "failed") continue;

    try {
      await apiFetch(mutation.path, {
        method: mutation.method,
        ...(mutation.body ? { body: mutation.body } : {}),
      });
      dequeue(mutation.id);
      succeeded++;
    } catch (err) {
      if (isHarmlessError(err)) {
        dequeue(mutation.id);
        succeeded++;
        continue;
      }

      const updatedQueue = getQueue();
      const idx = updatedQueue.findIndex((m) => m.id === mutation.id);
      if (idx < 0) continue;

      updatedQueue[idx].retries++;

      if (isServerRejection(err)) {
        updatedQueue[idx].status = "failed";
        updatedQueue[idx].lastError = (err as { message?: string }).message ?? "Erreur serveur";
        saveQueue(updatedQueue);
        rejected++;
      } else if (isNetworkError(err)) {
        saveQueue(updatedQueue);
        failed++;
        break; // Toujours hors-ligne, inutile de continuer
      } else {
        saveQueue(updatedQueue);
        failed++;
      }
    }
  }

  return { succeeded, failed, rejected };
}
